import { useState } from 'react'
import { Loader2, MessageSquare, Pencil, Plus, Trash2, Zap } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

import {
  TemplateForm,
  templateToFormValues,
  type TemplateFormValues,
} from '@/features/messaging/template-form'
import {
  useCreateTemplate,
  useDeleteTemplate,
  useTemplates,
  useUpdateTemplate,
} from '@/features/messaging/hooks'
import { TEMPLATE_TRIGGERS, type MessageTemplate } from '@/types/messaging'

function triggerLabel(template: MessageTemplate): string | null {
  if (!template.trigger_type) return null
  const label =
    TEMPLATE_TRIGGERS.find((t) => t.value === template.trigger_type)?.label ?? template.trigger_type
  return template.trigger_value ? `${label} → ${template.trigger_value.replace('_', ' ')}` : label
}

export function TemplatesTab() {
  const { data: templates, isLoading, isError } = useTemplates()
  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate()
  const deleteMutation = useDeleteTemplate()

  const [createOpen, setCreateOpen] = useState(false)
  const [editing, setEditing] = useState<MessageTemplate | null>(null)
  const [deleting, setDeleting] = useState<MessageTemplate | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  const handleCreate = (values: TemplateFormValues) => {
    setFormError(null)
    createMutation.mutate(values, {
      onSuccess: () => setCreateOpen(false),
      onError: (err) =>
        setFormError(err instanceof Error ? err.message : 'Failed to create template'),
    })
  }

  const handleUpdate = (values: TemplateFormValues) => {
    if (!editing) return
    setFormError(null)
    updateMutation.mutate(
      { id: editing.id, body: values },
      {
        onSuccess: () => setEditing(null),
        onError: (err) =>
          setFormError(err instanceof Error ? err.message : 'Failed to update template'),
      },
    )
  }

  const handleDelete = () => {
    if (!deleting) return
    deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) })
  }

  return (
    <>
      <Card>
        <CardHeader className="flex-row items-center justify-between space-y-0">
          <div className="space-y-1.5">
            <CardTitle className="flex items-center gap-2 text-base">
              <MessageSquare className="h-4 w-4" /> Message templates
            </CardTitle>
            <CardDescription>
              Reusable SMS bodies. Templates with a trigger send automatically when the event fires
              — one template per trigger, so a second one bound to the same trigger replaces the
              first.
            </CardDescription>
          </div>
          <Button onClick={() => setCreateOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> New Template
          </Button>
        </CardHeader>
        <CardContent>
          {isLoading && (
            <div className="flex flex-col gap-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          )}
          {isError && (
            <p className="py-6 text-center text-sm text-destructive">Failed to load templates.</p>
          )}
          {!isLoading && !isError && (!templates || templates.length === 0) && (
            <p className="py-12 text-center text-sm text-muted-foreground">
              No templates yet — create one to speed up texting.
            </p>
          )}
          {!isLoading && !isError && templates && templates.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Trigger</TableHead>
                  <TableHead>Body</TableHead>
                  <TableHead className="w-24 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t) => (
                  <TableRow key={t.id}>
                    <TableCell className="font-medium">{t.name}</TableCell>
                    <TableCell>
                      <Badge variant="secondary" className="capitalize">
                        {t.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {triggerLabel(t) ? (
                        <span className="flex items-center gap-1.5 text-sm">
                          <Zap className="h-3.5 w-3.5 text-amber-500" />
                          {triggerLabel(t)}
                        </span>
                      ) : (
                        <span className="text-sm text-muted-foreground">Manual</span>
                      )}
                    </TableCell>
                    <TableCell className="max-w-sm">
                      <span className="block truncate text-muted-foreground">{t.body}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="icon" onClick={() => setEditing(t)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => setDeleting(t)}
                        className="text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Dialog */}
      <Dialog
        open={createOpen}
        onOpenChange={(open) => {
          setCreateOpen(open)
          if (!open) setFormError(null)
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>New Template</DialogTitle>
            <DialogDescription>
              A reusable SMS body with optional auto-send trigger.
            </DialogDescription>
          </DialogHeader>
          <TemplateForm
            onSubmit={handleCreate}
            isPending={createMutation.isPending}
            submitLabel="Create Template"
          />
          {formError && <p className="text-sm text-destructive">{formError}</p>}
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={!!editing}
        onOpenChange={(open) => {
          if (!open) {
            setEditing(null)
            setFormError(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Template</DialogTitle>
          </DialogHeader>
          {editing && (
            <TemplateForm
              defaultValues={templateToFormValues(editing)}
              onSubmit={handleUpdate}
              isPending={updateMutation.isPending}
              submitLabel="Save Changes"
            />
          )}
          {formError && <p className="text-sm text-destructive">{formError}</p>}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!deleting} onOpenChange={(open) => !open && setDeleting(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete template?</DialogTitle>
            <DialogDescription>
              This permanently deletes “{deleting?.name}”. Sent messages that used it are kept.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
